import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Value } from "platejs";
import { Maximize2 } from "lucide-react";
import { Toolbar } from "./Toolbar";

import { PageCanvas } from "./PageCanvas";
import { PageNavigator } from "./PageNavigator";
import {
  createEmptyPage,
  newPageId,
  type BlockSelectionInfo,
  type PlateEditorState,
  type PlatePage,
  type ProposalPagesEditorProps,
  type RegenerateSectionRequest,
} from "./types";
import { findSectionForBlock, plateValueToMarkdown } from "@/features/revisr/utils/plateBlockUtils";
import { formatPageNumberLabel, getCoverTocPageCount } from "@/features/revisr/utils/pageNumbering";

const PAGE_WIDTH = 794; // A4 at 96dpi

/** Calculate the zoom level that fits the A4 page within the available width. */
function calcFitZoom(navCollapsed = false): number {
  const navWidth = window.innerWidth < 640 ? (navCollapsed ? 32 : 176) : 192;
  const scrollbarRoom = 24;
  const available = window.innerWidth - navWidth - scrollbarRoom;
  if (available < PAGE_WIDTH) {
    return Math.max(0.3, parseFloat((available / PAGE_WIDTH).toFixed(2)));
  }
  return 1;
}

const sanitizePages = (pages: PlatePage[] | undefined): PlatePage[] => {
  if (!pages || pages.length === 0) return [createEmptyPage("Page 1")];
  return pages.map((p, idx) => ({
    ...p,
    id: p.id ?? newPageId(),
    name: p.name ?? `Page ${idx + 1}`,
    canvasSize: p.canvasSize ?? { width: 794, height: 1123 },
    background: p.background ?? { type: "solid", color: "#ffffff" },
    plateValue: p.plateValue && p.plateValue.length > 0
      ? p.plateValue
      : [{ type: "p", children: [{ text: "" }] }],
  }));
};

/** Matches auto-generated page names like "Page 3" (from sectionsToPlatePages /
 *  createEmptyPage). Meaningful names (cover title, "Table of Contents", or
 *  user edits) do not match and are preserved. */
const AUTO_PAGE_NAME = /^Page\s+\d+$/;

/**
 * Renumber pages whose name is still the auto-generated "Page N" form so the
 * number reflects their absolute (1-based) position in the list. Called after a
 * page delete so removing the cover + TOC renumbers the remaining content pages
 * from "Page 3, Page 4, ..." to "Page 1, Page 2, ...".
 */
const renumberPages = (pages: PlatePage[]): PlatePage[] =>
  pages.map((page, idx) =>
    AUTO_PAGE_NAME.test(page.name) ? { ...page, name: 'Page ' + (idx + 1) } : page,
  );

export const ProposalPagesEditor: React.FC<ProposalPagesEditorProps> = ({
  config,
  initialData,
  onChange,
  onRegenerateSection,
  isRegenerating,
  className,
  footerMeta,
  onFooterChange,
}) => {
  const initialPages = useMemo(
    () => sanitizePages(initialData?.pages),
    // initialData is read once at mount; remount via parent `key` to reset
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [pages, setPages] = useState<PlatePage[]>(initialPages);
  const [currentPageId, setCurrentPageId] = useState<string>(
    initialData?.currentPageId ?? initialPages[0].id,
  );
  const [zoom, setZoom] = useState<number>(() => initialData?.zoom ?? calcFitZoom());
  // Incrementing a page's remount key forces PageCanvas to re-mount with
  // updated plateValue as its initial content (needed after overflow injection).
  const [pageRemountKeys, setPageRemountKeys] = useState<Record<string, number>>({});

  // Re-fit when viewport size changes (orientation change, resize)
  useEffect(() => {
    const onResize = () => setZoom(calcFitZoom());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const editorsMapRef = useRef<Map<string, unknown>>(new Map());
  const [activeEditor, setActiveEditor] = useState<unknown>(null);

  const previewMode =
    config?.viewMode === "preview" ||
    initialData?.previewMode === true;
  const showSidebar = config?.showSidebar !== false && !previewMode;

  // Stash the latest onChange in a ref so the emit effect doesn't re-fire on
  // every parent render (parents typically pass an inline lambda).
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onChangeRef.current?.({
      version: 2,
      pages,
      currentPageId,
      zoom,
      previewMode,
    });
  }, [pages, currentPageId, zoom, previewMode]);

  const handleValueChange = useCallback((pageId: string, value: Value) => {
    setPages((prev) =>
      prev.map((p) => (p.id === pageId ? { ...p, plateValue: value } : p)),
    );
  }, []);

  // Called by PageCanvas when its content overflows the A4 boundary.
  // Prepends the overflowed blocks to the next page (or creates one),
  // then force-remounts that page so it picks up the updated initial value.
  const handleOverflow = useCallback((pageId: string, overflowedBlocks: Value) => {
    setPages((prev) => {
      const pageIdx = prev.findIndex((p) => p.id === pageId);
      if (pageIdx === -1) return prev;

      const currentPage = prev[pageIdx];
      const newPages = [...prev];

      if (pageIdx < prev.length - 1) {
        // Prepend to the existing next page.
        const nextPage = prev[pageIdx + 1];
        const updatedNext: PlatePage = {
          ...nextPage,
          plateValue: [...overflowedBlocks, ...nextPage.plateValue] as Value,
        };
        newPages[pageIdx + 1] = updatedNext;
        // Force-remount the next page so it renders with the new initial value.
        setPageRemountKeys((keys) => ({
          ...keys,
          [nextPage.id]: (keys[nextPage.id] ?? 0) + 1,
        }));
      } else {
        // No next page — create one with the overflowed blocks.
        const newPage = createEmptyPage(`Page ${prev.length + 1}`);
        newPage.background = currentPage.background;
        newPage.canvasSize = currentPage.canvasSize;
        newPage.plateValue = overflowedBlocks;
        newPages.push(newPage);
        setCurrentPageId(newPage.id);
      }

      return newPages;
    });
  }, []);

  const handleAddPage = useCallback(() => {
    setPages((prev) => {
      const newPage = createEmptyPage(`Page ${prev.length + 1}`);
      // Inherit background from current page so design stays consistent.
      const current = prev.find((p) => p.id === currentPageId);
      if (current) {
        newPage.background = current.background;
        newPage.canvasSize = current.canvasSize;
      }
      setCurrentPageId(newPage.id);
      return [...prev, newPage];
    });
  }, [currentPageId]);

  const handleDeletePage = useCallback(
    (pageId: string) => {
      setPages((prev) => {
        if (prev.length <= 1) return prev;
        const next = prev.filter((p) => p.id !== pageId);
        if (pageId === currentPageId) {
          setCurrentPageId(next[0].id);
        }
        // Refresh auto-numbered ("Page N") names so they stay sequential and
        // reflect absolute position. Removing the cover + TOC (which leaves
        // content pages numbered "Page 3, Page 4, ...") renumbers them freshly
        // to "Page 1, Page 2, ...". Cover (named with the policy title), TOC
        // ("Table of Contents") and user-renamed pages are left untouched.
        return renumberPages(next);
      });
    },
    [currentPageId],
  );

  const handleEditorMount = useCallback((pageId: string, editor: unknown) => {
    if (editor === null) {
      editorsMapRef.current.delete(pageId);
    } else {
      editorsMapRef.current.set(pageId, editor);
    }
    // If this is the current page, update the active editor for the toolbar.
    setCurrentPageId((prev) => {
      if (pageId === prev) setActiveEditor(editor);
      return prev;
    });
  }, []);

  const handleSelectPage = useCallback((pageId: string) => {
    setCurrentPageId(pageId);
    setActiveEditor(editorsMapRef.current.get(pageId) ?? null);
    const el = document.querySelector(`[data-page-id="${pageId}"]`);
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const handleRenamePage = useCallback((pageId: string, name: string) => {
    setPages((prev) =>
      prev.map((p) => (p.id === pageId ? { ...p, name } : p)),
    );
  }, []);

  // Block selection tracking for regeneration
  const [selectedBlock, setSelectedBlock] = useState<BlockSelectionInfo | null>(null);

  const handleBlockSelect = useCallback((info: BlockSelectionInfo | null) => {
    setSelectedBlock(info);
  }, []);

  const handleRegenerateClick = useCallback(() => {
    if (!selectedBlock || !onRegenerateSection) return;

    const page = pages.find((p) => p.id === selectedBlock.pageId);
    if (!page) return;

    const sectionInfo = findSectionForBlock(page.plateValue, selectedBlock.blockIndex);
    if (!sectionInfo) return;

    // Build full document markdown from all pages for context
    const allMarkdown = pages
      .map((p) => plateValueToMarkdown(p.plateValue))
      .join('\n\n');

    onRegenerateSection({
      sectionTitle: sectionInfo.sectionTitle,
      sectionIndex: sectionInfo.sectionIndex,
      pageId: selectedBlock.pageId,
      fullDocumentMarkdown: allMarkdown,
    });
  }, [selectedBlock, pages, onRegenerateSection]);

  const handleZoomIn = () => setZoom((z) => Math.min(2, +(z + 0.1).toFixed(2)));
  const handleZoomOut = () => setZoom((z) => Math.max(0.5, +(z - 0.1).toFixed(2)));
  const handleZoomReset = () => setZoom(1);

  return (
    <div
      data-testid="plate-editor"
      className={`flex h-full w-full bg-gray-100 ${className ?? ""}`}
    >
      {showSidebar && (
        <PageNavigator
          pages={pages}
          currentPageId={currentPageId}
          readOnly={previewMode}
          onSelect={handleSelectPage}
          onAdd={handleAddPage}
          onDelete={handleDeletePage}
          onRename={handleRenamePage}
        />
      )}

      <div className="flex-1 flex flex-col overflow-hidden">
        {!previewMode && (
          <div className="shrink-0 flex items-center border-b border-gray-200 bg-white">
            {/* Toolbar — horizontally scrollable on mobile */}
            <div className="flex-1 min-w-0">
              <Toolbar editor={activeEditor as any} />
            </div>
            {/* Zoom controls — always visible, pinned right */}
            <div className="flex items-center gap-0.5 shrink-0 px-1 border-l border-gray-200 bg-white">
              <button
                type="button"
                onClick={handleZoomOut}
                className="px-1.5 py-1 rounded hover:bg-gray-100 text-gray-600 text-sm min-w-[24px] min-h-[32px] flex items-center justify-center"
                title="Zoom out"
              >
                −
              </button>
              <button
                type="button"
                onClick={handleZoomReset}
                className="px-1 py-1 rounded hover:bg-gray-100 text-gray-600 text-xs min-w-[36px] min-h-[32px] text-center tabular-nums"
                title="Reset to 100%"
              >
                {Math.round(zoom * 100)}%
              </button>
              <button
                type="button"
                onClick={handleZoomIn}
                className="px-1.5 py-1 rounded hover:bg-gray-100 text-gray-600 text-sm min-w-[24px] min-h-[32px] flex items-center justify-center"
                title="Zoom in"
              >
                +
              </button>
              {/* Fit to screen button */}
              <button
                type="button"
                onClick={() => setZoom(calcFitZoom())}
                className="px-1 py-1 rounded hover:bg-gray-100 text-gray-500 min-w-[28px] min-h-[32px] flex items-center justify-center"
                title="Fit to screen"
              >
                <Maximize2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-auto py-2 md:py-4">
          {(() => {
            // Cover/TOC prefix length derived from page list — 0 when removed.
            const coverTocCount = getCoverTocPageCount(pages);
            return pages.map((page, idx) => (
            <PageCanvas
              key={`${page.id}-${pageRemountKeys[page.id] ?? 0}`}
              page={page}
              isActive={page.id === currentPageId}
              readOnly={previewMode}
              zoom={zoom}
              pageNumberLabel={formatPageNumberLabel(idx, pages.length, coverTocCount)}
              footerMeta={footerMeta}
              onFooterChange={onFooterChange}
              onValueChange={handleValueChange}
              onFocus={(id) => {
                setCurrentPageId(id);
                setActiveEditor(editorsMapRef.current.get(id) ?? null);
              }}
              onEditorMount={handleEditorMount}
              onBlockSelect={onRegenerateSection ? handleBlockSelect : undefined}
              onRegenerateClick={onRegenerateSection ? handleRegenerateClick : undefined}
              isRegenerating={isRegenerating}
            />
            ));
          })()}
        </div>
      </div>
    </div>
  );
};

export default ProposalPagesEditor;
